import React from "react";
import { useSearchParams } from "react-router-dom";
import PageContainer from "../../../components/PageContainer";
import DataTable from "../../../components/DataTable";
import EmptyState from "../../../components/EmptyState";
import Pagination from "../../../components/Pagination";
import TableSkeleton from "../../../components/TableSkeleton";
import SearchInput from "../../../components/filters/SearchInput";
import EmptyValue from "../../../components/EmptyValue";
import { formatCurrency } from "../../../utils/format";
import { DEFAULT_PAGE_SIZE } from "../../../constants/pagination";
import useSalesOrders from "../../hooks/useSalesOrders";

// Sales orders — /v2/sales/outstanding.
const PARAM = { page: "page", q: "q" };

// The two states this endpoint models. It has no NOT_COD.
const COD_LABELS = { TO_BE_COLLECTED: "To be collected", COLLECTED: "Paid completely" };

// COLOUR ENCODES MONEY DIRECTION, and nothing else on this screen is coloured.
//
// Green (elm-600) is money received; crimson is money still owed. Both are the app's own
// values -- elm-600 rather than elm-500 because elm-500 is too light to read as text on
// white, and #dc143c is the crimson legacy already uses to flag a row that needs
// attention (src/components/orderTable/table-layout.jsx).
//
// A zero is deliberately GREY rather than coloured. On a collections screen the eye is
// hunting for what is still owed; a settled figure should recede, not compete.
const MONEY_IN = "#208f7b";
const MONEY_OWED = "#dc143c";

// Two decimals, not the shared default of zero: the API keeps these figures to paise on
// purpose, so rounding them here would show a different number from the one it computed.
const amount = (value, tone) => {
    if (value === null || value === undefined) return <EmptyValue />;
    const isZero = Number(value) === 0;
    return (
        <span
            className={`tabular-nums ${isZero ? "text-gray-400" : "font-semibold"}`}
            style={isZero ? undefined : { color: tone }}
        >
            {formatCurrency(value, { fractionDigits: 2 })}
        </span>
    );
};

/** Neutral figure -- the invoice is just the bill, neither owed nor received. */
const money = (value) =>
    value === null || value === undefined
        ? <EmptyValue />
        : <span className="tabular-nums">{formatCurrency(value, { fractionDigits: 2 })}</span>;

// A quiet pill so the COD state reads at a glance, tinted by money direction.
const COD_PILL = {
    TO_BE_COLLECTED: "bg-[#fdeaee]",
    COLLECTED: "bg-elm-50",
};
const COD_TEXT = {
    TO_BE_COLLECTED: MONEY_OWED,
    COLLECTED: MONEY_IN,
};

function SalesOrdersListPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const page = Number(searchParams.get(PARAM.page)) || 1;
    const search = searchParams.get(PARAM.q) || "";

    const query = useSalesOrders({ page, size: DEFAULT_PAGE_SIZE, search });

    const goToPage = (nextPage) => {
        const next = new URLSearchParams(searchParams);
        next.set(PARAM.page, nextPage);
        setSearchParams(next, { replace: true });
    };

    const onSearch = (value) => {
        const next = new URLSearchParams(searchParams);
        if (value) next.set(PARAM.q, value);
        else next.delete(PARAM.q);
        // Back to page 1 on a new term. Staying on page 12 of a result set that just
        // shrank to two pages is the classic way to show someone an empty table.
        next.delete(PARAM.page);
        setSearchParams(next, { replace: true });
    };

    // Headers carry the noun plus what it measures -- Invoice Amount / Paid Amount /
    // COD Amount -- and nothing more. The originals ("Total Invoice Amount", "COD Amount
    // To Be Collected") repeated words the column position already implies.
    //
    // NOTE "COD Amount" is the unpaid balance on this endpoint, NOT the sum of real
    // CASH_ON_DELIVERY payment rows -- see the header of useSalesOrders. The Outstanding
    // endpoint counts actual COD payments and would put a different number under the same
    // heading, so the two must not be read as one measure.
    //
    // "Sales Person" matches the column of the same name on the Orders tab, so one person
    // is described one way across the console.
    const columns = [
        {
            key: "orderId",
            label: "Order ID",
            // Legacy renders order and quotation ids in the brand green; matching it
            // makes the id read as the row's handle rather than as another number.
            render: (row) => <span className="font-semibold text-elm-600">{row.orderId}</span>,
        },
        {
            key: "patientName",
            label: "Name",
            // Bold black: the patient is who the row is about, so it anchors the row the
            // way the name column does on the Orders and Quotations tabs. Deliberately
            // NOT coloured -- green and crimson are reserved for money direction here.
            render: (row) => (
                row.patientName
                    ? <span className="font-bold text-black">{row.patientName}</span>
                    : <EmptyValue />
            ),
        },
        { key: "phone", label: "Phone", render: (row) => row.phone || <EmptyValue /> },
        { key: "totalInvoiceAmount", label: "Invoice Amount", align: "right", render: (row) => money(row.totalInvoiceAmount) },
        { key: "totalAmountPaid", label: "Paid Amount", align: "right", render: (row) => amount(row.totalAmountPaid, MONEY_IN) },
        {
            key: "codAmountToBeCollected",
            label: "COD Amount",
            align: "right",
            // The one figure this screen exists for, so it is the one that carries weight.
            render: (row) => amount(row.codAmountToBeCollected, MONEY_OWED),
        },
        {
            key: "codStatus",
            label: "COD Status",
            // Read-only: this screen reports COD state, it does not record collections.
            render: (row) =>
                row.codStatus ? (
                    <span
                        className={`inline-block whitespace-nowrap rounded-2xl px-3 py-1 text-sm font-sans ${COD_PILL[row.codStatus] || "bg-gray-100"}`}
                        style={{ color: COD_TEXT[row.codStatus] }}
                    >
                        {COD_LABELS[row.codStatus] || row.codStatus}
                    </span>
                ) : (
                    <EmptyValue />
                ),
        },
        {
            key: "salesAssigned",
            label: "Sales Person",
            // Whoever the order's genesis transition was attributed to. Null for orders
            // created with no staff involved (self-checkout, webhooks, cron).
            render: (row) => row.salesAssigned || <EmptyValue />,
        },
    ];

    const rows = query.data?.orders || [];
    const total = query.data?.total || 0;

    return (
        <PageContainer>
            {/* Heading matches the Quotations screen exactly -- h2, text-2xl font-semibold
                mb-4, no subtitle. The two sibling tabs in this console carry no
                descriptive paragraph, and one here made this screen read as a different
                kind of page. The title is "Outstanding Amount", the name in the sidebar:
                it briefly said "Orders", which collided with the actual Orders tab. */}
            <h2 className="text-2xl font-semibold mb-4">Outstanding Amount</h2>

            {/* w-[60%] to match the Quotations search box. SearchInput itself already
                carries legacy's own input styling (px-6 bg-slate-100 py-4, 1.1rem, no
                border) -- only the width differed, so it is constrained here rather than
                by changing a component three consoles share. */}
            <div className="w-[60%] mb-4">
                <SearchInput
                    value={search}
                    onChange={onSearch}
                    placeholder="Search order ID, name or phone number"
                />
            </div>

            {/* isIdle matters in react-query v3: a disabled query with no data is idle,
                not loading, and would otherwise fall through to the empty state. */}
            {query.isLoading || query.isIdle ? (
                <div className="bg-white rounded-2xl p-4">
                    <table className="w-full">
                        <tbody>
                            <TableSkeleton rows={8} cols={columns.length} />
                        </tbody>
                    </table>
                </div>
            ) : query.isError ? (
                <EmptyState
                    title="Couldn't load orders"
                    hint="The request failed. Refresh to try again."
                />
            ) : rows.length === 0 ? (
                <EmptyState
                    title="No orders match this search"
                    hint={search ? `Nothing found for "${search}". Try an order ID, a patient name, or a full phone number.` : undefined}
                />
            ) : (
                <>
                    <DataTable columns={columns} rows={rows} />
                    <Pagination
                        page={page}
                        size={DEFAULT_PAGE_SIZE}
                        total={total}
                        onChange={goToPage}
                    />
                </>
            )}
        </PageContainer>
    );
}

export default SalesOrdersListPage;
